import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Checkbox,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Radio,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  Textarea,
  Toolbar,
  tokens,
} from '@fluentui/react-components';
import {
  getPortalActiveRole,
  getPortalTenantId,
} from '../../auth/portalSession';
import {
  loadTenantOrderAccess,
  saveTenantOrderAccess,
  type TenantOrderAccess,
  type TenantOrderPolicy,
  type SaveTenantOrderAccess,
} from '../../services/tenantOrderAccessService';

const availabilityOptions = [
  { value: 'allowed', label: 'Enabled' },
  { value: 'disabled', label: 'Disabled' },
];
const sectionStyle = { display: 'grid', gap: tokens.spacingVerticalM };

export default function TenantReportAvailability({
  tenantId,
  selectedTenantId,
  categoriesOnly = false,
}: {
  tenantId?: number;
  selectedTenantId?: number;
  categoriesOnly?: boolean;
}) {
  if (getPortalActiveRole() !== 'FMSPortal.AdminAccess') return null;
  if (!tenantId)
    return <Text>Save the tenant before configuring report availability.</Text>;
  if (tenantId !== selectedTenantId || tenantId !== getPortalTenantId())
    return (
      <Text>
        Select this tenant in the tenant switcher to manage its report
        availability.
      </Text>
    );
  return (
    <AvailabilityEditor
      key={`${tenantId}-${categoriesOnly}`}
      categoriesOnly={categoriesOnly}
    />
  );
}
function AvailabilityEditor({ categoriesOnly }: { categoriesOnly: boolean }) {
  const subject = categoriesOnly ? 'Form categories' : 'Report availability';
  const changeReason = categoriesOnly
    ? 'Category change reason'
    : 'Availability change reason';
  const [data, setData] = useState<TenantOrderAccess>();
  const [policy, setPolicy] = useState<TenantOrderPolicy>();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const retry = useRef<SaveTenantOrderAccess | undefined>(undefined);
  const pendingWrite = useRef<AbortController | undefined>(undefined);
  const panel = useRef<HTMLElement>(null);
  const [height, setHeight] = useState<number>();

  // Fit the actual remaining viewport below the Settings/Pricing tabs.
  useLayoutEffect(() => {
    const resize = () => {
      if (panel.current)
        setHeight(
          Math.max(
            160,
            window.innerHeight - panel.current.getBoundingClientRect().top - 16
          )
        );
    };
    resize();
    window.addEventListener('resize', resize);
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(resize);
    if (panel.current?.parentElement)
      observer?.observe(panel.current.parentElement);
    return () => {
      window.removeEventListener('resize', resize);
      observer?.disconnect();
    };
  }, []);
  useEffect(() => () => pendingWrite.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    setData(undefined);
    setPolicy(undefined);
    setError('');
    setSuccess('');
    setReason('');
    retry.current = undefined;
    loadTenantOrderAccess(controller)
      .then((result) => {
        if (controller.signal.aborted) return;
        setData(result);
        setPolicy(
          result.policy ?? {
            enabled: false,
            categoryCodes: [],
            forms: result.forms.map((f) => ({
              formDefinitionId: f.id,
              requiredCategoryCodes: [],
              isAllowed: null,
            })),
          }
        );
      })
      .catch((e) => {
        if (!controller.signal.aborted)
          setError(e.message ?? `Unable to load ${subject.toLowerCase()}.`);
      });
    return () => controller.abort();
  }, [reloadKey, subject]);

  const save = async () => {
    if (!policy || !data || busy || !reason.trim()) return;
    setBusy(true);
    setError('');
    setSuccess('');
    const input: SaveTenantOrderAccess = {
      operationId: '',
      expectedRevision: data.revision,
      reason: reason.trim(),
      policy,
    };
    input.operationId =
      retry.current &&
      JSON.stringify({ ...retry.current, operationId: '' }) ===
        JSON.stringify(input)
        ? retry.current.operationId
        : crypto.randomUUID();
    retry.current = input;
    const controller = new AbortController();
    pendingWrite.current = controller;
    try {
      const result = await saveTenantOrderAccess(controller, input);
      if (controller.signal.aborted) return;
      setData(result);
      setPolicy(result.policy!);
      setReason('');
      setSuccess(`${subject} saved.`);
    } catch (e) {
      setError(
        (e as Error).message ?? `Unable to save ${subject.toLowerCase()}.`
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      ref={panel}
      aria-label={
        categoriesOnly ? 'Tenant form categories' : 'Tenant report availability'
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 1100,
        height: height ?? 'calc(100dvh - 260px)',
        minHeight: 160,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Toolbar
        aria-label={`${subject} actions`}
        style={{
          display: 'flex',
          gap: tokens.spacingHorizontalS,
          flexShrink: 0,
          padding: `${tokens.spacingVerticalS} 0`,
          backgroundColor: tokens.colorNeutralBackground1,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Button
          aria-label={
            categoriesOnly ? 'Reload form categories' : 'Reload availability'
          }
          disabled={busy}
          onClick={() => setReloadKey((k) => k + 1)}
        >
          Reload
        </Button>
        <Button
          aria-label={
            categoriesOnly ? 'Save form categories' : 'Save report availability'
          }
          appearance="primary"
          disabled={busy || !data || !policy || !reason.trim()}
          onClick={save}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </Toolbar>
      <div
        role="region"
        aria-label={`${subject} content`}
        tabIndex={0}
        style={{
          ...sectionStyle,
          alignContent: 'start',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          scrollbarGutter: 'stable',
          padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS} ${tokens.spacingVerticalL} 0`,
        }}
      >
        <Text as="h3" size={500} weight="semibold">
          {categoriesOnly ? 'Form categories' : 'Form availability'}
        </Text>
        <Text>
          {categoriesOnly
            ? 'Label and group this tenant’s forms. Categories do not affect form access.'
            : 'Enable or disable this tenant’s Order and Work forms.'}
        </Text>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>
              {error} Reload after a conflict before making a new change.
            </MessageBarBody>
          </MessageBar>
        )}
        {success && (
          <MessageBar intent="success">
            <MessageBarBody>{success}</MessageBarBody>
          </MessageBar>
        )}
        {!data && !error && (
          <Spinner
            size="small"
            label={
              categoriesOnly
                ? 'Loading form categories…'
                : 'Loading availability…'
            }
          />
        )}
        {data && policy && (
          <>
            <section aria-label="Tenant access" style={sectionStyle}>
              {!categoriesOnly && (
                <>
                  <Text as="h4" weight="semibold">
                    Tenant access
                  </Text>
                  <Checkbox
                    label="Use form availability rules"
                    checked={policy.enabled}
                    disabled={busy}
                    onChange={(_, value) =>
                      setPolicy({ ...policy, enabled: value.checked === true })
                    }
                  />
                  <Text>
                    {policy.enabled
                      ? 'Only enabled, listed forms are offered. Categories do not affect form access.'
                      : 'Existing form choices remain available. Enable these rules to restrict them.'}
                  </Text>
                </>
              )}
              <Text as="h4" weight="semibold">
                Order and Work choices
              </Text>
              <Text>
                {categoriesOnly
                  ? 'Create category definitions under Settings → Global settings → Categories.'
                  : 'Configure both the Order form and its intended Work forms.'}
              </Text>
              {data.forms.length === 0 && (
                <Text>
                  No Order or Work forms are configured for this tenant.
                </Text>
              )}
              <div style={{ overflowX: 'auto' }}>
                <Table
                  aria-label="Order and Work choices"
                  style={{ minWidth: 800 }}
                >
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Form</TableHeaderCell>
                      {!categoriesOnly &&
                        availabilityOptions.map((option) => (
                          <TableHeaderCell key={option.value}>
                            {option.label}
                          </TableHeaderCell>
                        ))}
                      {categoriesOnly && (
                        <TableHeaderCell>Form categories</TableHeaderCell>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.forms.map((form) => {
                      const rule = policy.forms.find(
                        (f) => f.formDefinitionId === form.id
                      );
                      const mode = !rule
                        ? 'disabled'
                        : rule.isAllowed === false
                        ? 'disabled'
                        : 'allowed';
                      const name = `${form.label} (${form.entityType})`;
                      const setMode = (value: string) => {
                        const others = policy.forms.filter(
                          (f) => f.formDefinitionId !== form.id
                        );
                        setPolicy({
                          ...policy,
                          forms: [
                            ...others,
                            {
                              formDefinitionId: form.id,
                              requiredCategoryCodes:
                                rule?.requiredCategoryCodes ?? [],
                              isAllowed: value === 'allowed',
                            },
                          ],
                        });
                      };
                      return (
                        <TableRow key={form.id}>
                          <TableHeaderCell scope="row" role="rowheader">
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Text weight="semibold">{name}</Text>
                              {!form.publishedMetadataId && (
                                <Text>No published version.</Text>
                              )}
                            </div>
                          </TableHeaderCell>
                          {!categoriesOnly &&
                            availabilityOptions.map((option) => (
                              <TableCell key={option.value}>
                                <Radio
                                  name={`form-availability-${form.id}`}
                                  aria-label={`${name} — ${option.label}`}
                                  value={option.value}
                                  checked={mode === option.value}
                                  disabled={busy}
                                  onChange={() => setMode(option.value)}
                                />
                              </TableCell>
                            ))}
                          {categoriesOnly && (
                            <TableCell>
                              <Dropdown
                                multiselect
                                aria-label={`${name} form categories`}
                                placeholder="No categories"
                                value={
                                  rule?.requiredCategoryCodes
                                    .map(
                                      (code) =>
                                        data.categories.find(
                                          (c) => c.code === code
                                        )?.label ?? code
                                    )
                                    .join(', ') ?? ''
                                }
                                selectedOptions={
                                  rule?.requiredCategoryCodes ?? []
                                }
                                disabled={busy}
                                style={{ width: 220, minWidth: 0 }}
                                onOptionSelect={(_, value) =>
                                  setPolicy({
                                    ...policy,
                                    forms: [
                                      ...policy.forms.filter(
                                        (f) => f.formDefinitionId !== form.id
                                      ),
                                      {
                                        formDefinitionId: form.id,
                                        isAllowed: rule
                                          ? rule.isAllowed
                                          : false,
                                        requiredCategoryCodes:
                                          value.selectedOptions,
                                      },
                                    ],
                                  })
                                }
                              >
                                {data.categories.map((c) => (
                                  <Option
                                    key={c.code}
                                    value={c.code}
                                    disabled={
                                      !c.isActive &&
                                      !rule?.requiredCategoryCodes.includes(
                                        c.code
                                      )
                                    }
                                  >
                                    {`${c.label}${
                                      !c.isActive ? ' (inactive)' : ''
                                    }`}
                                  </Option>
                                ))}
                              </Dropdown>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <Field label={changeReason} required>
                <Textarea
                  aria-label={changeReason}
                  maxLength={1000}
                  value={reason}
                  disabled={busy}
                  resize="vertical"
                  onChange={(_, value) => setReason(value.value)}
                />
              </Field>
            </section>
            <Accordion collapsible>
              <AccordionItem value="history">
                <AccordionHeader>
                  Change history ({data.history.length})
                </AccordionHeader>
                <AccordionPanel>
                  {[...data.history].reverse().map((h) => (
                    <div
                      key={h.revision}
                      style={{ marginBottom: tokens.spacingVerticalM }}
                    >
                      <Text as="p">
                        Revision {h.revision} · {h.createdAt} · Administrator{' '}
                        {h.createdBy}
                      </Text>
                      <Text as="p">{h.reason}</Text>
                    </div>
                  ))}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>
        )}
      </div>
    </section>
  );
}
